import { Plus } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';

import type { RuleFieldDef, RuleValueKind } from '../schemas/rule-fields';
import { emptyCondition, MAX_CONDITIONS, type RulesFormHost } from '../schemas/rules-form-schema';

import { RuleConditionRow } from './rule-condition-row';

export interface RulesBuilderProps {
  fields: readonly RuleFieldDef[];
  kindOf: (field: string) => RuleValueKind;
  /** Currency of the price fields: the store's for a collection, the base currency for a pool. */
  currency: string;
  title: string;
  description?: string;
  tour?: string;
}

/**
 * Match all / any, one row per condition, add up to twenty. Lives inside a react-hook-form whose
 * values hold the rule set under `rules` (`RulesFormHost`).
 */
export function RulesBuilder({
  fields,
  kindOf,
  currency,
  title,
  description,
  tour,
}: RulesBuilderProps) {
  const { t } = useTranslation('productRules');
  const form = useFormContext<RulesFormHost>();
  const conditions = useFieldArray({ control: form.control, name: 'rules.conditions' });
  const match = form.watch('rules.match');
  // A form built with `disabled` (no write permission, an ended schedule) locks the whole builder.
  const disabled = form.formState.disabled;
  const conditionsError = form.formState.errors.rules?.conditions;
  const conditionsMessage =
    conditionsError && 'message' in conditionsError && typeof conditionsError.message === 'string'
      ? conditionsError.message
      : conditionsError?.root?.message;

  return (
    <FormSection title={title} description={description} tour={tour}>
      <div className="flex flex-wrap items-center gap-3">
        <Label id="rules-match-label">{t('rules.match')}</Label>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={match}
          aria-labelledby="rules-match-label"
          disabled={disabled}
          onValueChange={(v) => {
            if (v === 'all' || v === 'any') {
              form.setValue('rules.match', v, { shouldDirty: true });
            }
          }}
        >
          <ToggleGroupItem value="all">{t('rules.matchAll')}</ToggleGroupItem>
          <ToggleGroupItem value="any">{t('rules.matchAny')}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="flex flex-col gap-3">
        {conditions.fields.map((row, index) => (
          <RuleConditionRow
            key={row.id}
            index={index}
            fields={fields}
            kindOf={kindOf}
            currency={currency}
            canRemove={!disabled && conditions.fields.length > 1}
            disabled={disabled}
            onRemove={() => conditions.remove(index)}
          />
        ))}
      </div>
      {conditionsMessage ? (
        <p className="text-destructive text-sm" role="alert">
          {conditionsMessage}
        </p>
      ) : null}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || conditions.fields.length >= MAX_CONDITIONS}
          onClick={() => conditions.append(emptyCondition())}
        >
          <Plus aria-hidden />
          {t('rules.addCondition')}
        </Button>
        {conditions.fields.length >= MAX_CONDITIONS ? (
          <span className="text-muted-foreground text-xs">
            {t('rules.max', { count: MAX_CONDITIONS })}
          </span>
        ) : null}
      </div>
    </FormSection>
  );
}
